import TagManager from 'react-gtm-module';
import _once from 'lodash/once';
import _map from 'lodash/map';
import _mapKeys from 'lodash/mapKeys';
import _ from 'lodash';
import { noCase, snakeCase } from 'change-case-all';
import noop from 'lodash/noop';
import Router, { NextRouter } from 'next/router';

import PublicEnv from 'shared/utils/public-env';
import { ORDER_TYPE_DELIVERY, ORDER_TYPE_PICKUP } from 'shared/constants';
import { removeNullishValues } from 'shared/utils/remove-nullish-values';

import { tracker } from 'src/utils/analytics';
import { getCategory } from 'src/hooks/use-dispensary-category';
import { maybePushEcommerceToDatalayer } from 'src/utils/ecommerce-analytics';
import { EVENT_ACTIONS } from 'src/payments/hooks';
import { EnrollmentSteps } from 'src/payments/constants';
import { AnalyticsContext, EventTracker } from '../../events';
import { GTM_V2_FLAG } from '../../constants/feature-flags';
import {
  cartItemContext,
  cartItemToGTMCartItem,
  ecommerceGTMMetaData,
  getDispensaryContext,
  getUserContext,
  getCartContext,
  getCheckoutToken,
  getOrderDiscount,
  getCartDiscount,
  getOrderTotalTax,
  getCommonEcommerceContext,
  getPurchaseType,
} from './helpers/internal-gtm-converters';
import { getPageType } from './helpers/get-page-type';
import { injectItemIndex } from './helpers/item-list-name';
import { injectPageType } from './helpers/inject-page-type';
import { ContextCache, GA4_EVENTS, Payload } from './internal-gtm-tracker.types';
import { convertPromotionToGTM } from './helpers/convert-promotion-to-gtm';
import { createCreativeSlot } from './helpers/create-creative-slot';

export const DATA_LAYER_KEY = 'EcommDataLayer';

const setupClient = _once(() =>
  TagManager.initialize({
    gtmId: PublicEnv.gtmContainerKey,
    dataLayerName: DATA_LAYER_KEY,
    dataLayer: {
      'gtm.whitelist': ['google', 'customPixels', 'flc', 'fls', 'twitter_website_tag', 'customScripts'],
    },
  })
);

const initInternalGTMTracker = (enabled): any => {
  if (!enabled) {
    return;
  }

  setupClient();
};

// Generally you shouldn't be using this directly, but we needed it for a one-off case of tracking the experiment framework ENG-37271
export function pushInternalGTMDataLayer(payload: Payload, key: string): void {
  if (!window[key]) {
    window[key] = [];
  }
  if (window[key]) {
    window[key].push(payload);
  }
}

type TrackerArgs = {
  enabled: boolean;
};

/**
 * Convert menu variant to channel name
 * @param {string} menuVariant - The menu variant from analytics context
 * @returns {'storefront'|'kiosk'|'embedded'|'mobileApp'|'default'} - The channel name
 */
function getChannelFromMenuVariant(menuVariant: string): string {
  switch (menuVariant) {
    case 'store-front':
      return 'storefront';
    case 'kiosk':
      return 'kiosk';
    case 'embedded':
      return 'embedded';
    case 'default':
    default:
      return 'default';
  }
}

export const createInternalGTMTracker = ({ enabled }: TrackerArgs): EventTracker => {
  initInternalGTMTracker(enabled);

  // Helper to get router instance (converts null to undefined for type compatibility)
  const getRouter = (): NextRouter | undefined => Router.router ?? undefined;

  function pushToDataLayer(payload: Payload, key = DATA_LAYER_KEY): void {
    if (enabled) {
      // Add channel information to all events
      const enhancedPayload = {
        ...payload,
        channel: getChannelFromMenuVariant(contextCache.activeMenuVariant),
      };

      // clearing ecommerce data if it exists
      // https://developers.google.com/analytics/devguides/collection/ua/gtm/enhanced-ecommerce#clear-ecommerce
      if (enhancedPayload.ecommerce) {
        window[key].push({ ecommerce: null });
      }

      pushInternalGTMDataLayer(enhancedPayload, key);
    } else {
      console.warn('Error pushing to datalayer, no GTM instance available.');
    }
  }

  const contextCache: ContextCache = {
    activeCart: null,
    activeDispensary: null,
    activePath: null,
    activeUser: null,
    activeMenuVariant: '',
    activeOrder: null,
    activePathHasChanged: false,
    cartDiscountSummary: null,
  };

  function handlePageView(newContext: AnalyticsContext, oldContext: AnalyticsContext): void {
    if (newContext.activePath?.concrete === oldContext.activePath?.concrete) {
      return;
    }
    const dispensaryContextNotLoadedYet = !newContext.activeDispensary;
    const dispensaryNeededAndAvailable = newContext.activeDispensary;
    const userContextNotLoadedYet = !newContext.activeUser;
    const userNeededAndAvailable = newContext.activeUser;
    const cartContextNotLoadedYet = !newContext.activeCart;
    const cartNeededAndAvailable = newContext.activeCart;

    const dispensaryContext =
      !dispensaryContextNotLoadedYet || dispensaryNeededAndAvailable
        ? getDispensaryContext(newContext, contextCache)
        : {};

    const userContext =
      !userContextNotLoadedYet || userNeededAndAvailable ? getUserContext(newContext, contextCache) : {};

    const cartContext =
      !cartContextNotLoadedYet || cartNeededAndAvailable ? getCartContext(newContext, contextCache) : {};

    // Push to standard GTM dataLayer
    const payload: Payload = {
      event: GA4_EVENTS.pageView,
      ...removeNullishValues(dispensaryContext),
      ...removeNullishValues(userContext),
      ...removeNullishValues(cartContext),
      page_url: window.location.href,
      page_path: newContext.activePath?.concrete,
      data_source: newContext.activeMenuVariant,
      page_title: document.title,
      page_type: getPageType(getRouter()),
    };
    maybePushEcommerceToDatalayer(payload);

    // Push to ecommerce dataLayer
    pushToDataLayer(payload);
  }

  function updateContextCache({ contextUpdates }): void {
    Object.keys(contextUpdates).forEach((key) => {
      if (key in contextCache) {
        contextCache[key] = contextUpdates[key];
      }
    });
  }

  function getCategoryValue(context: AnalyticsContext): string | null {
    return (
      getCategory(
        getRouter()?.query.category?.toString()?.toLowerCase(),
        context.activeDispensary ?? contextCache.activeDispensary
      )?.value ?? null
    );
  }

  return {
    onContextChange: (updates, newContext, oldContext) => {
      updateContextCache({ contextUpdates: updates });

      if (newContext.activePath?.concrete !== oldContext.activePath?.concrete) {
        handlePageView(newContext, oldContext);
      }
    },

    // Login Events
    accountLogin(payload, context) {
      const { newAccount, customerId, analyticsEventLabel } = payload;
      const page = getPageType(getRouter());
      pushToDataLayer({
        event: GA4_EVENTS.gtmEvent,
        event_category: `account`,
        event_action: newAccount ? `created account` : `signed in`,
        event_label: analyticsEventLabel ?? page?.toLowerCase(),
        customer_id: customerId,
        ...getDispensaryContext(context, contextCache),
      });
    },

    loyaltySignup(payload, context) {
      const { analyticsEventLabel } = payload;
      pushToDataLayer({
        event: GA4_EVENTS.gtmEvent,
        event_category: `account`,
        event_action: 'loyalty signup',
        event_label: analyticsEventLabel,
        ...getUserContext(context, contextCache),
        ...getDispensaryContext(context, contextCache),
      });
    },

    // Menu Events
    addedProductToCart(payload, context) {
      const useV2 = !!context.featureFlags?.[GTM_V2_FLAG];
      const itemAdded = {
        ...cartItemToGTMCartItem(payload, useV2),
        affiliation: context.activeDispensary?.name,
      };

      injectItemIndex({ context, GTMItem: itemAdded });

      if (context.activePromotion) {
        itemAdded.item_list_id = context.activePromotion.campaignId;
        itemAdded.item_list_name = context.activePromotion.trackerSource;
        itemAdded.creative_slot = createCreativeSlot({ router: getRouter(), category: getCategoryValue(context) });
        tracker.setContext({ activePromotion: null });
      }

      updateContextCache({ contextUpdates: context });

      const ecommMeta = ecommerceGTMMetaData({ context, contextCache, useV2 });

      const checkoutToken = getCheckoutToken(context, contextCache);
      const commonEcommerce = getCommonEcommerceContext(context, contextCache);
      const purchaseTypeAtc = getPurchaseType(context, contextCache);

      if (useV2) {
        const itemPrice = itemAdded.price;
        if (itemPrice == null) {
          console.warn('[GTM] addedProductToCart: could not resolve item price', itemAdded.item_id);
        }
        const itemValue = parseFloat(((Number(itemPrice) || 0) * (Number(itemAdded.quantity) || 1)).toFixed(2));

        pushToDataLayer({
          event: GA4_EVENTS.addToCart,
          ...getDispensaryContext(context, contextCache),
          checkout_id: checkoutToken,
          purchase_type: purchaseTypeAtc,
          ecommerce: {
            ...ecommMeta,
            value: itemValue,
            ...commonEcommerce,
            items: [itemAdded],
          },
        });
      } else {
        pushToDataLayer({
          event: GA4_EVENTS.addToCart,
          ...getDispensaryContext(context, contextCache),
          checkout_id: checkoutToken,
          purchase_type: purchaseTypeAtc,
          ecommerce: {
            ...ecommMeta,
            ...commonEcommerce,
            items: [itemAdded],
          },
        });
      }
    },
    cartUpdated(payload, context) {
      const useV2 = !!context.featureFlags?.[GTM_V2_FLAG];
      if (!useV2) return;

      const items = _map(payload.products ?? [], (item) => ({
        ...cartItemContext(context, contextCache),
        ...cartItemToGTMCartItem(item, true),
      }));
      const checkoutToken = getCheckoutToken(context, contextCache);
      const commonEcommerce = getCommonEcommerceContext(context, contextCache);
      pushToDataLayer({
        event: GA4_EVENTS.cartUpdated,
        ...getDispensaryContext(context, contextCache),
        checkout_id: checkoutToken,
        delivery_method: payload.deliveryMethod,
        payment_type: payload.paymentType,
        purchase_type: payload.purchaseType,
        is_scheduled: payload.isScheduled,
        ecommerce: {
          ...ecommerceGTMMetaData({ context, contextCache, payload, useV2: true }),
          ...commonEcommerce,
          items,
        },
      });
    },
    cartViewed(payload, context) {
      const useV2 = !!context.featureFlags?.[GTM_V2_FLAG];
      const items = _map(payload.products, (item) => ({
        ...cartItemContext(context, contextCache, payload),
        ...cartItemToGTMCartItem(useV2 ? { product: item } : item, useV2),
      }));
      const checkoutToken = getCheckoutToken(context, contextCache);
      const commonEcommerce = getCommonEcommerceContext(context, contextCache);
      pushToDataLayer({
        event: GA4_EVENTS.viewCart,
        ...getDispensaryContext(context, contextCache),
        checkout_id: checkoutToken,
        purchase_type: getPurchaseType(context, contextCache),
        ecommerce: {
          ...ecommerceGTMMetaData({ context, contextCache, payload, useV2 }),
          ...commonEcommerce,
          items,
        },
      });
    },
    productClicked(payload, context) {
      const useV2 = !!context.featureFlags?.[GTM_V2_FLAG];
      const productSelected = {
        ...cartItemToGTMCartItem(payload, useV2),
        affiliation: context.activeDispensary?.name,
      };

      injectItemIndex({ context, GTMItem: productSelected });

      pushToDataLayer({
        event: GA4_EVENTS.selectItem,
        page_type: getPageType(getRouter()),
        ...getDispensaryContext(context, contextCache),
        ecommerce: {
          ...ecommerceGTMMetaData({ context, contextCache, useV2 }),
          items: [productSelected],
        },
      });
    },
    removedProductFromCart(payload, context) {
      const useV2 = !!context.featureFlags?.[GTM_V2_FLAG];
      const itemRemoved = cartItemToGTMCartItem(payload, useV2);
      const ecommMeta = ecommerceGTMMetaData({ context, contextCache, useV2 });
      const checkoutToken = getCheckoutToken(context, contextCache);
      const commonEcommerce = getCommonEcommerceContext(context, contextCache);
      const purchaseTypeRfc = getPurchaseType(context, contextCache);

      if (useV2) {
        const itemPrice = itemRemoved.price;
        if (itemPrice == null) {
          console.warn('[GTM] removedProductFromCart: could not resolve item price', itemRemoved.item_id);
        }
        const itemValue = parseFloat(((Number(itemPrice) || 0) * (Number(itemRemoved.quantity) || 1)).toFixed(2));

        pushToDataLayer({
          event: GA4_EVENTS.removeFromCart,
          ...getDispensaryContext(context, contextCache),
          checkout_id: checkoutToken,
          purchase_type: purchaseTypeRfc,
          ecommerce: {
            ...ecommMeta,
            value: itemValue,
            ...commonEcommerce,
            items: [itemRemoved],
          },
        });
      } else {
        pushToDataLayer({
          event: GA4_EVENTS.removeFromCart,
          ...getDispensaryContext(context, contextCache),
          checkout_id: checkoutToken,
          purchase_type: purchaseTypeRfc,
          ecommerce: {
            ...ecommMeta,
            ...commonEcommerce,
            items: [itemRemoved],
          },
        });
      }
    },
    searchedForProduct(payload) {
      pushToDataLayer({
        event: GA4_EVENTS.search,
        search_term: payload.query,
      });
    },
    searchResults(payload, context) {
      const { query, hasResults, products } = payload;
      const useV2 = !!context.featureFlags?.[GTM_V2_FLAG];

      if (useV2 && products?.length) {
        const items = _map(products, (p) => cartItemToGTMCartItem({ product: p }, true));
        pushToDataLayer({
          event: GA4_EVENTS.viewSearchResults,
          search_term: query.toLowerCase(),
          ecommerce: { items },
          ...getDispensaryContext(context, contextCache),
        });
      }

      pushToDataLayer({
        event: GA4_EVENTS.gtmEvent,
        event_category: `search`,
        event_action: hasResults ? `search results` : `no search results`,
        event_label: query.toLowerCase(),
        ...getDispensaryContext(context, contextCache),
      });
    },
    viewedProduct(payload, context) {
      const useV2 = !!context.featureFlags?.[GTM_V2_FLAG];
      const productSelected = {
        ...cartItemToGTMCartItem(payload, useV2),
        ...cartItemContext(context, contextCache),
        affiliation: context.activeDispensary?.name,
      };

      injectItemIndex({ context, GTMItem: productSelected });

      const ecommMeta = ecommerceGTMMetaData({ context, contextCache, useV2 });

      if (useV2) {
        const itemPrice = Number(productSelected.price) || 0;
        const itemValue = parseFloat((itemPrice * (Number(productSelected.quantity) || 1)).toFixed(2));

        pushToDataLayer({
          event: GA4_EVENTS.viewItem,
          ...getDispensaryContext(context, contextCache),
          ecommerce: {
            ...ecommMeta,
            value: itemValue,
            items: [productSelected],
          },
        });
      } else {
        pushToDataLayer({
          event: GA4_EVENTS.viewItem,
          ...getDispensaryContext(context, contextCache),
          ecommerce: {
            ...ecommMeta,
            items: [productSelected],
          },
        });
      }
    },
    // Menu Events > Ads & Personalization
    buyItAgainImpression(payload, context) {
      payload.analyticsPage = createCreativeSlot({ router: getRouter(), category: getCategoryValue(context) });
      const convertedPayload = convertPromotionToGTM(payload);

      pushToDataLayer({
        event: GA4_EVENTS.viewItemList,
        ...getUserContext(context, contextCache),
        ...getDispensaryContext(context, contextCache),
        ecommerce: {
          ...ecommerceGTMMetaData({ context, contextCache }),
          ...convertedPayload,
        },
      });
    },
    buyItAgainViewAllClicked(payload, context) {
      const { destinationUrl, trackerSource } = payload;
      pushToDataLayer({
        event: GA4_EVENTS.gtmEvent,
        event_category: 'content',
        event_action: 'click',
        event_label: 'buy it again view all',
        click_url: destinationUrl,
        data_source: trackerSource,
        ...getUserContext(context, contextCache),
        ...getDispensaryContext(context, contextCache),
      });
    },
    sponsoredProductImpression(payload, context) {
      payload.analyticsPage = createCreativeSlot({ router: getRouter(), category: getCategoryValue(context) });
      const convertedPayload = convertPromotionToGTM(payload);

      pushToDataLayer({
        event: GA4_EVENTS.viewPromotion,
        ...getDispensaryContext(context, contextCache),
        ecommerce: {
          ...ecommerceGTMMetaData({ context, contextCache }),
          ...convertedPayload,
        },
      });
    },
    sponsoredProductClicked(payload, context) {
      payload.analyticsPage = createCreativeSlot({ router: getRouter(), category: getCategoryValue(context) });
      const convertedPayload = convertPromotionToGTM(payload);

      pushToDataLayer({
        event: GA4_EVENTS.selectPromotion,
        ...getDispensaryContext(context, contextCache),
        ecommerce: {
          ...ecommerceGTMMetaData({ context, contextCache }),
          ...convertedPayload,
        },
      });
    },
    sponsoredBannerClicked: noop,
    sponsoredBannerImpression(payload, context) {
      payload.analyticsPage = createCreativeSlot({ router: getRouter(), category: getCategoryValue(context) });
      const convertedPayload = convertPromotionToGTM(payload);

      pushToDataLayer({
        event: GA4_EVENTS.viewItemList,
        ...getDispensaryContext(context, contextCache),
        ecommerce: {
          ...ecommerceGTMMetaData({ context, contextCache }),
          ...convertedPayload,
        },
      });
    },
    privacyPreferenceUpdated(payload, context) {
      pushToDataLayer({
        event: GA4_EVENTS.gtmEvent,
        event_category: 'privacy',
        event_action: payload.action,
        event_label: payload.preference,
        ...getUserContext(context, contextCache),
        ...getDispensaryContext(context, contextCache),
      });
    },

    // Checkout Events
    placedOrder(payload, context) {
      const useV2 = !!context.featureFlags?.[GTM_V2_FLAG];
      const items = _map(context.activeOrder?.cart, (item) => ({
        ...cartItemContext(context, contextCache),
        ...cartItemToGTMCartItem(item, useV2),
      }));
      const checkoutToken = getCheckoutToken(context, contextCache);
      const orderDiscount = getOrderDiscount(payload.order);
      const discount =
        orderDiscount != null && orderDiscount > 0 ? orderDiscount : getCartDiscount(context, contextCache);
      pushToDataLayer({
        event: GA4_EVENTS.purchase,
        ...getDispensaryContext(context, contextCache),
        checkout_id: checkoutToken,
        order_id: payload.order.orderId ?? undefined,
        is_kiosk_order: context.isKioskOrder,
        payment_type: payload.order.paymentMethod ?? null,
        delivery_method: payload.order.deliveryInfo?.deliveryOption ? ORDER_TYPE_DELIVERY : ORDER_TYPE_PICKUP,
        purchase_type: payload.order.medicalOrder ? 'medicinal' : 'recreational',
        user_enrolled_dutchie_pay: payload.isEnrolledDutchiePay,
        dutchie_pay_enabled: payload.isDutchiePayEnabledForDispo,
        email: payload.order.customer?.emails?.[0] ?? payload.order.guestCustomer?.email ?? null,
        ecommerce: {
          transaction_id: payload.order.orderId,
          order_id: payload.order.orderId ?? undefined,
          checkout_id: checkoutToken,
          tax: payload.order.taxAmount,
          total_tax: getOrderTotalTax(payload.order),
          shipping: payload.order.deliveryFee ?? null,
          coupon: context.activeOrder?.coupon?.code ?? null,
          discount,
          ...ecommerceGTMMetaData({ context, contextCache, useV2 }),
          items,
        },
      });
    },
    viewedCheckout(payload, context) {
      const useV2 = !!context.featureFlags?.[GTM_V2_FLAG];
      updateContextCache({ contextUpdates: context });
      const items = _map(context.activeOrder?.cart, (item) => ({
        ...cartItemContext(context, contextCache),
        ...cartItemToGTMCartItem(item, useV2),
      }));
      const commonEcommerce = getCommonEcommerceContext(context, contextCache);
      pushToDataLayer({
        event: GA4_EVENTS.beginCheckout,
        ...getDispensaryContext(context, contextCache),
        ...getUserContext(context, contextCache),
        checkout_id: payload.checkoutToken,
        dutchie_pay_enabled: payload.isDutchiePayEnabledForDispo,
        user_enrolled_dutchie_pay: payload.isEnrolledDutchiePay,
        is_kiosk_order: context.isKioskOrder,
        purchase_type: getPurchaseType(context, contextCache),
        payment_type: context.activeOrder?.paymentMethod ?? contextCache.activeOrder?.paymentMethod ?? null,
        ecommerce: {
          ...ecommerceGTMMetaData({ context, contextCache, useV2 }),
          ...commonEcommerce,
          tax: payload.tax,
          total_tax: payload.tax,
          items,
        },
      });
    },
    checkoutSession: _.once((payload, context) => {
      const useV2 = !!context.featureFlags?.[GTM_V2_FLAG];
      updateContextCache({ contextUpdates: context });
      const items = _map(context.activeOrder?.cart, (item) => ({
        ...cartItemContext(context, contextCache),
        ...cartItemToGTMCartItem(item, useV2),
      }));
      const commonEcommerce = getCommonEcommerceContext(context, contextCache);
      pushToDataLayer({
        event: GA4_EVENTS.checkoutSession,
        ...getDispensaryContext(context, contextCache),
        ...getUserContext(context, contextCache),
        checkout_id: payload.checkoutToken,
        dispensary_id: payload.dispensaryId,
        dispensary_name: payload.dispensaryName,
        dutchie_pay_enabled: payload.isDutchiePayEnabledForDispo,
        user_enrolled_dutchie_pay: payload.isEnrolledDutchiePay,
        customer_id: payload.customerId,
        logged_in: payload.loggedIn,
        purchase_type: getPurchaseType(context, contextCache),
        payment_type: context.activeOrder?.paymentMethod ?? contextCache.activeOrder?.paymentMethod ?? null,
        ecommerce: {
          ...ecommerceGTMMetaData({ context, contextCache, useV2 }),
          ...commonEcommerce,
          tax: payload.tax,
          items,
        },
      });
    }),
    imageBannerClicked(_payload, context) {
      pushToDataLayer({
        event: GA4_EVENTS.gtmEvent,
        event_category: 'content',
        event_action: 'click',
        event_label: 'image banner',
        ...getDispensaryContext(context, contextCache),
      });
    },
    checkoutStarted: noop,
    paymentsError(payload) {
      pushToDataLayer({
        event: GA4_EVENTS.gtmEvent,
        event_category: 'dutchiepay',
        event_action: 'payments error',
        event_label: payload.reasonCode,
        ..._mapKeys(payload.meta, (_value, key) => snakeCase(key)),
      });
    },
    dutchiePayEnrollmentButtonClicked(payload, context) {
      pushToDataLayer({
        event: GA4_EVENTS.gtmEvent,
        event_category: 'dutchiepay',
        event_action: `enrollment button clicked`,
        event_label: '',
        ...getDispensaryContext(context, contextCache),
        checkout_id: getCheckoutToken(context, contextCache),
        dutchie_pay_session_token: payload.dutchiePaySessionToken,
        dutchie_pay_enabled: true,
        button_location: payload.buttonLocation,
      });
    },
    dutchiePayEnrollmentStep(payload, context) {
      const isEnrolledDutchiePay =
        context.activeIsUserEnrolledDutchiePay ||
        (payload.action === EVENT_ACTIONS.SUCCEEDED && payload.step === EnrollmentSteps.completeEnrollment);

      pushToDataLayer({
        event: GA4_EVENTS.gtmEvent,
        event_category: 'dutchiepay',
        event_action: `enrollment step ${noCase(payload.action)}`,
        event_label: `step: ${noCase(payload.step)}`,
        ...getDispensaryContext(context, contextCache),
        checkout_id: getCheckoutToken(context, contextCache),
        dutchie_pay_session_token: payload.dutchiePaySessionToken,
        dutchie_pay_enabled: true,
        is_kiosk_order: context.isKioskOrder,
        user_enrolled_dutchie_pay: isEnrolledDutchiePay,
      });
    },
    dutchiePayEnrollmentStepCancelled: noop,
    dutchiePayCTAButtonClicked(payload, context) {
      pushToDataLayer({
        event: GA4_EVENTS.gtmEvent,
        event_category: 'dutchiepay',
        event_action: `learn more cta button clicked`,
        event_label: '',
        ...getDispensaryContext(context, contextCache),
        checkout_id: getCheckoutToken(context, contextCache),
        dutchie_pay_session_token: payload.dutchiePaySessionToken,
        dutchie_pay_enabled: true,
      });
    },

    dutchiePayPlaidOnEvent(payload, context) {
      pushToDataLayer({
        event: GA4_EVENTS.gtmEvent,
        event_category: 'dutchiepay',
        event_action: `connect bank step: plaid onEvent`,
        event_label: `${noCase(payload.eventName)}`,
        ...getDispensaryContext(context, contextCache),
        checkout_id: getCheckoutToken(context, contextCache),
        dutchie_pay_session_token: payload.dutchiePaySessionToken,
        dutchie_pay_enabled: true,
        is_kiosk_order: context.isKioskOrder,
        link_customization_name: payload.connectionMethod,
        ...payload.eventMetadata,
      });
    },
    dutchiePayPlaidOnLoad(payload, context) {
      pushToDataLayer({
        event: GA4_EVENTS.gtmEvent,
        event_category: 'dutchiepay',
        event_action: `connect bank step: plaid onLoad`,
        event_label: '',
        ...getDispensaryContext(context, contextCache),
        link_customization_name: payload.connectionMethod,
      });
    },
    dutchiePayServiceFeeShown: noop,
    dutchiePayIncentiveExposure: noop,
    dutchiePayPaymentMethodSelected: noop,
    dutchiePayPlaidOnSuccess(payload, context) {
      pushToDataLayer({
        event: GA4_EVENTS.gtmEvent,
        event_category: 'dutchiepay',
        event_action: `connect bank step: plaid onSuccess`,
        event_label: '',
        ...getDispensaryContext(context, contextCache),
        link_customization_name: payload.connectionMethod,
        ...payload.eventMetadata,
      });
    },
    dutchiePayPlaidOnExit(payload, context) {
      pushToDataLayer({
        event: GA4_EVENTS.gtmEvent,
        event_category: 'dutchiepay',
        event_action: `connect bank step: plaid onExit`,
        event_label: '',
        ...getDispensaryContext(context, contextCache),
        link_customization_name: payload.connectionMethod,
        ...payload.eventMetadata,
      });
    },
    dutchiePayAeroSyncOnEvent(payload, context) {
      pushToDataLayer({
        event: GA4_EVENTS.gtmEvent,
        event_category: 'dutchiepay',
        event_action: `connect bank step: aerosync onEvent`,
        event_label: `${noCase(payload.eventName)}`,
        ...getDispensaryContext(context, contextCache),
        checkout_id: getCheckoutToken(context, contextCache),
        dutchie_pay_session_token: payload.dutchiePaySessionToken,
        dutchie_pay_enabled: true,
        is_kiosk_order: context.isKioskOrder,
        ...payload.eventMetadata,
      });
    },

    dutchiePayPreAuthEvent(payload, context) {
      pushToDataLayer({
        event: GA4_EVENTS.gtmEvent,
        event_category: 'dutchiepay',
        event_action: `aerosync pre auth event`,
        event_label: `${noCase(payload.eventName)}`,
        ...getDispensaryContext(context, contextCache),
        checkout_id: getCheckoutToken(context, contextCache),
        dutchie_pay_session_token: payload.dutchiePaySessionToken,
        dutchie_pay_enabled: true,
        is_kiosk_order: context.isKioskOrder,
        ...payload.eventMetadata,
      });
    },

    dutchiePayInstoreAccountLinkViewed(payload) {
      const linkMethod = payload.showBarcode ? 'barcode' : 'manual code';

      pushToDataLayer({
        event: GA4_EVENTS.gtmEvent,
        event_category: 'dutchiepay',
        event_action: 'instore cart account link viewed',
        event_label: `method: ${linkMethod}`,
        instore_cart_token: payload.instoreCartToken,
      });
    },
    dutchiePayInstoreCartApprovalClicked(payload, context) {
      pushToDataLayer({
        event: GA4_EVENTS.gtmEvent,
        event_category: 'dutchiepay',
        event_action: 'instore cart approval clicked',
        event_label: '',
        checkout_id: getCheckoutToken(context, contextCache),
        dispensary_id: payload.dispensary.id,
        dispensary_name: payload.dispensary.name,
        instore_cart_id: payload.instoreCartId,
        instore_cart_token: payload.instoreCartToken,
        is_kiosk_order: context.isKioskOrder,
      });
    },
    dutchiePayInstoreCartApprovalFailed(payload, context) {
      pushToDataLayer({
        event: GA4_EVENTS.gtmEvent,
        event_category: 'dutchiepay',
        event_action: 'instore cart approval failed',
        event_label: '',
        checkout_id: getCheckoutToken(context, contextCache),
        dispensary_id: payload.dispensary.id,
        dispensary_name: payload.dispensary.name,
        instore_cart_id: payload.instoreCartId,
        instore_cart_token: payload.instoreCartToken,
        is_kiosk_order: context.isKioskOrder,
      });
    },
    dutchiePayInstoreCartApprovalSucceeded(payload) {
      pushToDataLayer({
        event: GA4_EVENTS.gtmEvent,
        event_category: 'dutchiepay',
        event_action: 'instore cart approval succeeded',
        event_label: '',
        dispensary_id: payload.dispensary.id,
        dispensary_name: payload.dispensary.name,
        instore_cart_id: payload.instoreCartId,
        instore_cart_token: payload.instoreCartToken,
      });
    },
    dutchiePayInstoreCartViewed(payload, context) {
      pushToDataLayer({
        event: GA4_EVENTS.gtmEvent,
        event_category: 'dutchiepay',
        event_action: 'instore cart viewed',
        checkout_id: getCheckoutToken(context, contextCache),
        event_label: '',
        dispensary_id: payload.dispensary.id,
        dispensary_name: payload.dispensary.name,
        instore_cart_token: payload.instoreCartToken,
        is_kiosk_order: context.isKioskOrder,
      });
    },
    dutchiePayInstoreLoginViewed(payload, context) {
      pushToDataLayer({
        event: GA4_EVENTS.gtmEvent,
        event_category: 'dutchiepay',
        event_action: 'instore cart login viewed',
        event_label: `step: ${noCase(payload.loginStep)}`,
        checkout_id: getCheckoutToken(context, contextCache),
        instore_cart_token: payload.instoreCartToken,
        is_kiosk_order: context.isKioskOrder,
      });
    },
    gaGTMClickEvent(payload, context) {
      const pageType = getPageType(getRouter());
      pushToDataLayer({
        event: GA4_EVENTS.gtmEvent,
        event_category: injectPageType(payload.eventCategory, pageType)?.toLowerCase(),
        event_action: injectPageType(payload.eventAction, pageType)?.toLowerCase(),
        event_label: injectPageType(payload.eventLabel, pageType)?.toLowerCase(),
        ...getUserContext(context, contextCache),
        ...getDispensaryContext(context, contextCache),
      });
    },
    dispensaryClicked(payload, context) {
      const { dispensaryId, dispensaryName, dispensaryAddress, dispensaryChainName, source, meta } = payload;
      console.log('dispensaryClicked', payload);
      pushToDataLayer({
        event: GA4_EVENTS.gtmEvent,
        event_category: 'dispensary',
        event_action: 'click',
        event_label: source,
        ...getUserContext(context, contextCache),
        ...getDispensaryContext(context, contextCache),
        dispensary_id: dispensaryId,
        dispensary_name: dispensaryName,
        dispensary_address: dispensaryAddress,
        dispensary_chain_name: dispensaryChainName,
        ...(meta ?? {}),
      });
    },
    dutchiePayAeroSyncOnLoad: noop,
    dutchiePayAeroSyncOnClose: noop,
    dutchiePayAeroSyncOnSuccess: noop,
    dutchiePayAeroSyncOnError: noop,
    dutchiePayRelinkButtonViewed: noop,
    dutchiePayRelinkButtonClicked: noop,
    dutchiePayUnlinkRemoveCtaClicked: noop,
    dutchiePayUnlinkModalViewed: noop,
    dutchiePayUnlinkConfirmed: noop,
    dutchiePayUnlinkCancelled: noop,
    dutchiePayUnlinkSuccess: noop,
    dutchiePayUnlinkError: noop,
    dutchiePayKioskQRCodeModalViewed: noop,
    viewedKioskCheckoutModal: noop,
    loginNudge: noop,
    loginGateShown: noop,
    loginEntryPointClicked: noop,
    dutchiePayCheckoutFlowAction: noop,
    roundupForCharityViewed: noop,
    roundupForCharityToggled: noop,
    walletPass: noop,
    dutchiePayLinkToPayInitiated: noop,
    dutchiePayLinkToPayIneligible: noop,
    dutchiePayEcommModalPaymentSwitchViewed: noop,
    dutchiePayEcommModalPaymentSwitchClicked: noop,
    dutchiePayEcommModalPaymentSwitchOutcome: noop,
    dutchiePayLoyaltyCtaClicked: noop,
    productRatingViewed: noop,
    productReviewsViewed: noop,
    productReviewAiSummaryViewed: noop,
    productReviewStatusChecked: noop,
    productReviewCtaClicked: noop,
    productReviewSubmitted: noop,
    // Instore Enrollment events
    instoreEnrollmentStepViewed(payload, context) {
      pushToDataLayer({
        event: GA4_EVENTS.gtmEvent,
        event_category: 'instore_enrollment',
        event_action: 'step_viewed',
        event_label: payload.step,
        ...getDispensaryContext(context, contextCache),
      });
    },
    instoreEnrollmentStepCompleted(payload, context) {
      pushToDataLayer({
        event: GA4_EVENTS.gtmEvent,
        event_category: 'instore_enrollment',
        event_action: 'step_completed',
        event_label: payload.step,
        ...getDispensaryContext(context, contextCache),
      });
    },
    instoreEnrollmentStepSkipped(payload, context) {
      pushToDataLayer({
        event: GA4_EVENTS.gtmEvent,
        event_category: 'instore_enrollment',
        event_action: 'step_skipped',
        event_label: payload.step,
        ...getDispensaryContext(context, contextCache),
      });
    },
    medCardSignupCompleted(payload, context) {
      pushToDataLayer({
        event: GA4_EVENTS.gtmEvent,
        event_category: 'instore_enrollment',
        event_action: 'med_card_signup_completed',
        event_label: '',
        waiting_at_dispensary: payload.waitingAtDispensary,
        ...getDispensaryContext(context, contextCache),
      });
    },
    medCardSignupFailed(payload, context) {
      pushToDataLayer({
        event: GA4_EVENTS.gtmEvent,
        event_category: 'instore_enrollment',
        event_action: 'med_card_signup_failed',
        event_label: payload.errorType,
        error_type: payload.errorType,
        ...getDispensaryContext(context, contextCache),
      });
    },
    conciergeEntryPointClicked: noop,
    conciergeImpression: noop,
    conciergeAction: noop,
    conciergeMessageSent: noop,
    conciergeExited: noop,
    conciergeBundleAdded: noop,
    conciergeAccessGate: noop,
    conciergeConversationRefreshed: noop,
    conciergeMessageStopped: noop,
    conciergeError: noop,
    conciergeIdleWarning: noop,
    accountSignupAction: noop,
    loyaltyEnrollmentAction: noop,
    phoneVerificationNudgeAction: noop,
    onboardingModalStep: noop,
    onboardingFlowStarted: noop,
  };
};
